// A company's registration (or taxpayer) number and its VAT number: the minimal rule for each country
// (TF-231, docs/adr/0160-company-number-rules.md). One table for everyone who takes a number: the app's
// forms (on leaving the field), the services (which never trust a form) and the website's demo form
// (TF-241, a copy of this file).
//
// Minimal on purpose: what characters, how many, an example - no check-digit arithmetic. Whether a
// number is real is for VIES (TF-33) and the country registers (TF-42..44). Spaces, dots and hyphens
// are ignored and the number is kept compact: "lv 4000 3123-456" is "LV40003123456".
//
// This file has no imports and runs in Node and in browsers alike: keep it that way.

export type NumberKind = 'registration' | 'vat';
export type NumberLanguage = 'en' | 'lv';

type Words = Record<NumberLanguage, string>;

export interface NumberRule {
  /** Tested on the compact number; for a VAT number, with its country prefix. */
  pattern: RegExp;
  /** What the number looks like, in words: "11 digits", "LV + 11 digits". */
  shape: Words;
  /** An example as people write it. */
  example: string;
}

/** Capitals and digits only: "lv 4000 3123-456" -> "LV40003123456". */
export const compactNumber = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9]/g, '');

const digits = (count: string | number): Words => ({ en: `${count} digits`, lv: `${count} cipari` });
const words = (en: string, lv: string): Words => ({ en, lv });

/** The EU's member states (ISO codes: Greece is GR here, EL only as a VAT prefix). */
const EU = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE',
  'SI', 'SK',
];
/** The EU and the rest of the EEA. */
const EEA = new Set<string>([...EU, 'IS', 'LI', 'NO']);

// ---------------------------------------------------------------------------- registration numbers

const REGISTRATION: Record<string, NumberRule> = {
  // A company's register number (40003...) or a person's taxpayer code (personas kods).
  LV: { pattern: /^\d{11}$/, shape: digits(11), example: '40003123456' },
  // Registry code, or a person's code.
  EE: { pattern: /^(\d{8}|\d{11})$/, shape: words('8 or 11 digits', '8 vai 11 cipari'), example: '12345678' },
  // Company code (7 for old ones), or a person's code.
  LT: { pattern: /^(\d{7}|\d{9}|\d{11})$/, shape: words('9 digits (7 for old codes, 11 for a person)', '9 cipari (vecajiem kodiem 7, personai 11)'), example: '123456789' },
  // Y-tunnus: 7 digits, a hyphen and a check digit.
  FI: { pattern: /^\d{8}$/, shape: words('7 digits, a hyphen and a check digit', '7 cipari, defise un kontrolcipars'), example: '1234567-8' },
  // Organisationsnummer.
  SE: { pattern: /^\d{10}$/, shape: digits(10), example: '556123-4567' },
  // CVR.
  DK: { pattern: /^\d{8}$/, shape: digits(8), example: '12345678' },
  // Organisasjonsnummer.
  NO: { pattern: /^\d{9}$/, shape: digits(9), example: '123456789' },
  // NIP or KRS.
  PL: { pattern: /^\d{10}$/, shape: digits(10), example: '1234567890' },
  // A commercial register entry (HRB 12345, perhaps with the court's letters), or a tax number.
  DE: {
    pattern: /^(HR[AB]\d{1,6}[A-Z]{0,3}|\d{10,13})$/,
    shape: words('HRA or HRB and its number, or a tax number of 10 to 13 digits', 'HRA vai HRB un tā numurs, vai nodokļu numurs ar 10 līdz 13 cipariem'),
    example: 'HRB 12345',
  },
  // KvK.
  NL: { pattern: /^\d{8}$/, shape: digits(8), example: '12345678' },
};

const REGISTRATION_EEA: NumberRule = {
  pattern: /^(?=.*\d)[A-Z0-9]{4,20}$/,
  shape: words('4 to 20 letters and digits, at least one digit', '4 līdz 20 burti un cipari, vismaz viens cipars'),
  example: '12345678',
};
const REGISTRATION_ANY: NumberRule = {
  pattern: /^(?=.*\d)[A-Z0-9]{4,30}$/,
  shape: words('4 to 30 letters and digits, at least one digit', '4 līdz 30 burti un cipari, vismaz viens cipars'),
  example: '12345678',
};

/** The rule for a registration or taxpayer number of a company in this country (ISO code). */
export function registrationRule(country: string | null | undefined): NumberRule {
  const code = (country ?? '').toUpperCase();
  return REGISTRATION[code] ?? (EEA.has(code) ? REGISTRATION_EEA : REGISTRATION_ANY);
}

// ----------------------------------------------------------------------------------- VAT numbers

/** The prefix of a country's VAT numbers: Greece's is EL. */
export const vatPrefix = (country: string) => (country.toUpperCase() === 'GR' ? 'EL' : country.toUpperCase());

/** What follows the prefix, as VIES describes each member state's numbers (Norway: its MVA number). */
const VAT_BODY: Record<string, { pattern: string; shape: Words; example: string }> = {
  AT: { pattern: 'U\\d{8}', shape: words('U and 8 digits', 'U un 8 cipari'), example: 'U12345678' },
  BE: { pattern: '[01]\\d{9}', shape: digits(10), example: '0123456789' },
  BG: { pattern: '\\d{9,10}', shape: words('9 or 10 digits', '9 vai 10 cipari'), example: '123456789' },
  CY: { pattern: '\\d{8}[A-Z]', shape: words('8 digits and a letter', '8 cipari un burts'), example: '12345678X' },
  CZ: { pattern: '\\d{8,10}', shape: words('8 to 10 digits', '8 līdz 10 cipari'), example: '12345678' },
  DE: { pattern: '\\d{9}', shape: digits(9), example: '123456789' },
  DK: { pattern: '\\d{8}', shape: digits(8), example: '12345678' },
  EE: { pattern: '\\d{9}', shape: digits(9), example: '123456789' },
  EL: { pattern: '\\d{9}', shape: digits(9), example: '123456789' },
  ES: { pattern: '[A-Z0-9]\\d{7}[A-Z0-9]', shape: words('9 letters and digits', '9 burti un cipari'), example: 'B12345678' },
  FI: { pattern: '\\d{8}', shape: digits(8), example: '12345678' },
  FR: { pattern: '[A-Z0-9]{2}\\d{9}', shape: words('2 letters or digits and 9 digits', '2 burti vai cipari un 9 cipari'), example: '12345678901' },
  HR: { pattern: '\\d{11}', shape: digits(11), example: '12345678901' },
  HU: { pattern: '\\d{8}', shape: digits(8), example: '12345678' },
  IE: { pattern: '\\d{7}[A-Z]{1,2}|\\d[A-Z]\\d{5}[A-Z]', shape: words('7 digits and 1 or 2 letters', '7 cipari un 1 vai 2 burti'), example: '1234567WA' },
  IT: { pattern: '\\d{11}', shape: digits(11), example: '12345678901' },
  LT: { pattern: '\\d{9}|\\d{12}', shape: words('9 or 12 digits', '9 vai 12 cipari'), example: '123456789' },
  LU: { pattern: '\\d{8}', shape: digits(8), example: '12345678' },
  LV: { pattern: '\\d{11}', shape: digits(11), example: '40003123456' },
  MT: { pattern: '\\d{8}', shape: digits(8), example: '12345678' },
  NL: { pattern: '\\d{9}B\\d{2}', shape: words('9 digits, B and 2 digits', '9 cipari, B un 2 cipari'), example: '123456789B01' },
  NO: { pattern: '\\d{9}MVA', shape: words('9 digits and MVA', '9 cipari un MVA'), example: '123456789MVA' },
  PL: { pattern: '\\d{10}', shape: digits(10), example: '1234567890' },
  PT: { pattern: '\\d{9}', shape: digits(9), example: '123456789' },
  RO: { pattern: '\\d{2,10}', shape: words('2 to 10 digits', '2 līdz 10 cipari'), example: '12345678' },
  SE: { pattern: '\\d{12}', shape: digits(12), example: '123456789001' },
  SI: { pattern: '\\d{8}', shape: digits(8), example: '12345678' },
  SK: { pattern: '\\d{10}', shape: digits(10), example: '1234567890' },
  // The United Kingdom, and Northern Ireland's XI numbers that VIES knows.
  GB: { pattern: '\\d{9}|\\d{12}|GD\\d{3}|HA\\d{3}', shape: words('9 or 12 digits', '9 vai 12 cipari'), example: '123456789' },
  XI: { pattern: '\\d{9}|\\d{12}|GD\\d{3}|HA\\d{3}', shape: words('9 or 12 digits', '9 vai 12 cipari'), example: '123456789' },
};

const VAT_ANY: NumberRule = {
  pattern: /^[A-Z]{2}(?=[A-Z0-9]*\d)[A-Z0-9]{2,15}$/,
  shape: words('two letters, then 2 to 15 letters and digits', 'divi burti, tad 2 līdz 15 burti un cipari'),
  example: 'XX123456789',
};

/** The rule for a VAT number with this prefix (EL for Greece, XI for Northern Ireland). */
function vatRuleOfPrefix(prefix: string): NumberRule {
  const body = VAT_BODY[prefix];
  if (!body) return VAT_ANY;
  return {
    pattern: new RegExp(`^${prefix}(${body.pattern})$`),
    shape: { en: `${prefix} + ${body.shape.en}`, lv: `${prefix} + ${body.shape.lv}` },
    example: `${prefix}${body.example}`,
  };
}

/** The rule for a VAT number of a company in this country. */
export function vatRule(country: string | null | undefined): NumberRule {
  return country ? vatRuleOfPrefix(vatPrefix(country)) : VAT_ANY;
}

// ------------------------------------------------------------------------------------- checking

export type NumberCheck =
  | { ok: true; value: string | null }
  /** `prefix`: a VAT number of another country than the company's. */
  | { ok: false; problem: 'format' | 'prefix'; rule: NumberRule };

/** A registration or taxpayer number: compact, or what is wrong with it. Empty is fine (null). */
export function checkRegistrationNumber(country: string | null | undefined, raw: string | null | undefined): NumberCheck {
  const value = compactNumber(raw ?? '');
  if (!value) return { ok: true, value: null };
  const rule = registrationRule(country);
  return rule.pattern.test(value) ? { ok: true, value } : { ok: false, problem: 'format', rule };
}

/**
 * A VAT number, compact and with its country's prefix (added when it was typed without one), or what is
 * wrong with it. A company in the United Kingdom may have Northern Ireland's XI number.
 */
export function checkVatNumber(country: string | null | undefined, raw: string | null | undefined): NumberCheck {
  const compact = compactNumber(raw ?? '');
  if (!compact) return { ok: true, value: null };
  if (!country) return VAT_ANY.pattern.test(compact) ? { ok: true, value: compact } : { ok: false, problem: 'format', rule: VAT_ANY };
  const own = vatPrefix(country);
  const given = (own === 'GB' ? ['GB', 'XI'] : [own]).find((prefix) => compact.startsWith(prefix));
  if (given) {
    const rule = vatRuleOfPrefix(given);
    return rule.pattern.test(compact) ? { ok: true, value: compact } : { ok: false, problem: 'format', rule };
  }
  // Typed without its prefix: "40003123456" for a Latvian company.
  const rule = vatRuleOfPrefix(own);
  if (rule.pattern.test(`${own}${compact}`)) return { ok: true, value: `${own}${compact}` };
  // Two letters in front that are not the country's: another country's number.
  return { ok: false, problem: /^[A-Z]{2}/.test(compact) ? 'prefix' : 'format', rule };
}

/** A number of either kind. */
export const checkCompanyNumber = (kind: NumberKind, country: string | null | undefined, raw: string | null | undefined) =>
  kind === 'registration' ? checkRegistrationNumber(country, raw) : checkVatNumber(country, raw);

// ------------------------------------------------------------------------------------- in words

const countryName = (country: string, language: NumberLanguage) => {
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(country.toUpperCase()) ?? country;
  } catch {
    return country;
  }
};

/** What the field expects, for its placeholder: "11 digits", "LV + 11 digits". */
export const numberShape = (kind: NumberKind, country: string | null | undefined, language: NumberLanguage = 'en') =>
  (kind === 'registration' ? registrationRule(country) : vatRule(country)).shape[language];

/**
 * What was expected, with the example: "A registration or taxpayer number in Latvia has 11 digits,
 * for example 40003123456."
 */
export function numberMessage(kind: NumberKind, country: string | null | undefined, check: Extract<NumberCheck, { ok: false }>, language: NumberLanguage = 'en'): string {
  const { rule } = check;
  const place = country ? countryName(country, language) : null;
  if (check.problem === 'prefix' && country) {
    const prefix = vatPrefix(country);
    return language === 'lv'
      ? `Uzņēmuma no valsts ${place} PVN numurs sākas ar ${prefix}, piemēram, ${rule.example}.`
      : `A VAT number of a company in ${place} starts with ${prefix}, for example ${rule.example}.`;
  }
  if (language === 'lv') {
    const what = kind === 'registration' ? 'Reģistrācijas vai nodokļu maksātāja numurs' : 'PVN numurs';
    return `${what}${place ? ` (${place})` : ''}: ${rule.shape.lv}, piemēram, ${rule.example}.`;
  }
  const what = kind === 'registration' ? 'A registration or taxpayer number' : 'A VAT number';
  return `${what}${place ? ` in ${place}` : ''} has ${rule.shape.en}, for example ${rule.example}.`;
}
